import { motion } from "framer-motion";

/** Dashboard preview mock: schedule timeline + checklist + photo tiles (Login / right side) */
export function AuthIllustrationLogin() {
  return (
    <motion.div
      className="relative w-full max-w-[280px] max-h-[160px] mx-auto flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      aria-hidden
    >
      <svg
        viewBox="0 0 320 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full object-contain"
      >
        <defs>
          <linearGradient id="auth-card-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--primary) / 0.12)" />
            <stop offset="100%" stopColor="hsl(var(--primary) / 0.04)" />
          </linearGradient>
          <filter id="auth-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="8" floodOpacity="0.08" />
          </filter>
        </defs>
        {/* Schedule timeline cards */}
        <rect x="20" y="20" width="120" height="32" rx="6" fill="url(#auth-card-grad)" stroke="hsl(var(--border))" strokeWidth="1" filter="url(#auth-shadow)" />
        <rect x="20" y="58" width="100" height="28" rx="6" fill="url(#auth-card-grad)" stroke="hsl(var(--border))" strokeWidth="1" opacity="0.9" />
        <rect x="20" y="92" width="110" height="28" rx="6" fill="url(#auth-card-grad)" stroke="hsl(var(--border))" strokeWidth="1" opacity="0.8" />
        {/* Checklist cards */}
        <rect x="160" y="20" width="140" height="48" rx="8" fill="hsl(var(--card))" stroke="hsl(var(--border))" strokeWidth="1" filter="url(#auth-shadow)" />
        <circle cx="178" cy="38" r="4" fill="hsl(var(--primary))" />
        <rect x="192" y="34" width="80" height="6" rx="2" fill="hsl(var(--muted-foreground) / 0.4)" />
        <circle cx="178" cy="54" r="4" fill="hsl(var(--muted-foreground) / 0.3)" />
        <rect x="192" y="50" width="60" height="6" rx="2" fill="hsl(var(--muted-foreground) / 0.3)" />
        {/* Photo upload tiles */}
        <rect x="20" y="136" width="56" height="44" rx="6" fill="hsl(var(--primary) / 0.15)" stroke="hsl(var(--primary) / 0.4)" strokeWidth="1" strokeDasharray="4 2" />
        <path d="M48 152v8M44 156h8" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinecap="round" />
        <rect x="84" y="136" width="56" height="44" rx="6" fill="hsl(var(--muted))" stroke="hsl(var(--border))" strokeWidth="1" />
        <rect x="148" y="136" width="56" height="44" rx="6" fill="hsl(var(--muted))" stroke="hsl(var(--border))" strokeWidth="1" />
      </svg>
    </motion.div>
  );
}

/** Workflow: Calendar → Task → Cleaner → Done (Sign Up / left side) */
export function AuthIllustrationSignUp() {
  return (
    <motion.div
      className="relative w-full max-w-[280px] max-h-[160px] mx-auto flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      aria-hidden
    >
      <svg
        viewBox="0 0 320 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full object-contain"
      >
        <defs>
          <linearGradient id="auth-workflow-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--primary) / 0.2)" />
            <stop offset="100%" stopColor="hsl(var(--primary) / 0.05)" />
          </linearGradient>
          <filter id="auth-blur-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.1" />
          </filter>
        </defs>
        {/* Step 1: Calendar */}
        <motion.g
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
        >
          <rect x="24" y="48" width="56" height="56" rx="12" fill="url(#auth-workflow-grad)" stroke="hsl(var(--primary) / 0.5)" strokeWidth="1" filter="url(#auth-blur-shadow)" />
          <rect x="36" y="58" width="32" height="4" rx="2" fill="hsl(var(--primary))" />
          <rect x="36" y="68" width="24" height="4" rx="2" fill="hsl(var(--muted-foreground) / 0.5)" />
          <rect x="36" y="78" width="28" height="4" rx="2" fill="hsl(var(--muted-foreground) / 0.5)" />
        </motion.g>
        <path d="M88 76 L128 76" stroke="hsl(var(--primary) / 0.4)" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 4" />
        {/* Step 2: Task */}
        <motion.g
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        >
          <rect x="136" y="48" width="56" height="56" rx="12" fill="url(#auth-workflow-grad)" stroke="hsl(var(--primary) / 0.5)" strokeWidth="1" filter="url(#auth-blur-shadow)" />
          <circle cx="164" cy="68" r="6" fill="hsl(var(--primary))" />
          <rect x="148" y="82" width="32" height="4" rx="2" fill="hsl(var(--muted-foreground) / 0.6)" />
        </motion.g>
        <path d="M200 76 L240 76" stroke="hsl(var(--primary) / 0.4)" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 4" />
        {/* Step 3: Cleaner */}
        <motion.g
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
        >
          <rect x="248" y="48" width="56" height="56" rx="12" fill="url(#auth-workflow-grad)" stroke="hsl(var(--primary) / 0.5)" strokeWidth="1" filter="url(#auth-blur-shadow)" />
          <circle cx="276" cy="66" r="8" fill="hsl(var(--primary) / 0.6)" />
          <rect x="260" y="80" width="32" height="4" rx="2" fill="hsl(var(--muted-foreground) / 0.6)" />
        </motion.g>
        <path d="M164 112 L164 136" stroke="hsl(var(--primary) / 0.4)" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 4" />
        {/* Step 4: Done */}
        <motion.g
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
        >
          <rect x="136" y="144" width="56" height="40" rx="10" fill="hsl(var(--primary) / 0.2)" stroke="hsl(var(--primary))" strokeWidth="1" />
          <path d="M152 164 L162 174 L176 158" stroke="hsl(var(--primary))" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </motion.g>
        {/* Labels — Task pushed down so vertical dashed line (y 112–136) doesn't overlap */}
        <text x="44" y="118" className="fill-muted-foreground" style={{ fontSize: 10, fontFamily: "system-ui" }}>Calendar</text>
        <text x="152" y="140" style={{ fontSize: 10, fontFamily: "system-ui", fill: "hsl(var(--muted-foreground))" }}>Task</text>
        <text x="260" y="118" style={{ fontSize: 10, fontFamily: "system-ui", fill: "hsl(var(--muted-foreground))" }}>Cleaner</text>
        <text x="148" y="198" style={{ fontSize: 10, fontFamily: "system-ui", fill: "hsl(var(--muted-foreground))" }}>Done</text>
      </svg>
    </motion.div>
  );
}
